import { reactive } from "vue";

import { CancelError } from "@/error/CancelError";
import { UnauthenticatedError } from "@/error/UnauthenticatedError";
import { handleError } from "@/error/errorHandler";
import { captureException } from "@/error/sentry";
import { DAYS } from "@/model/Settings";
import type { AlmMapping, AlmUser, AuthUser, TechnicalUser, UserOrgEntities } from "@/model/user";
import {
  almUser,
  backendUser,
  baseUser,
  emptyUserOrgEntities,
  isBackendUserId,
} from "@/model/user";
import { LocalStore, versioned } from "@/utils/LocalStore";
import { SimpleCache } from "@/utils/SimpleCache";
import { authAPIUrl } from "@/utils/env/Environment";

import { getAlmInfo } from "../read/BackendReader";
import type { ServerAuthUser, ServerUserOrgEntities } from "../serverModel";
import { createApiClient, modernInterceptor } from "./api.config";
import { isHttpError } from "./api.config.axios";
import { mapOrgEntities, mapUser, mapUserName } from "./mapBackendData";

const baseURL = "/v1/users";

const api = modernInterceptor(createApiClient(authAPIUrl));

const users = new SimpleCache<AuthUser>({
  store: new LocalStore("users", { sedes: versioned(2) }),
  timeout: DAYS,
});

// TODO caller is to debug network problems in get user, remove when solved
type UserIdentifier = ({ id: string } | TechnicalUser) & { caller: string };

export function loadReactiveUser(
  user: UserIdentifier & { name?: string },
  options: { almMapping?: AlmMapping; mapBackendToAlm?: boolean } = {},
): AuthUser {
  const res = reactive(uninitedUser(user, "loading"));
  loadUser(user, options)
    .then((loaded) => {
      res.type = loaded.type || "regular";
      res.id = loaded.id;
      res.name = loaded.name;
      res.email = loaded.email;
      res.createdAt = loaded.createdAt;
      res.imageUrl = loaded.imageUrl;
      res.color = loaded.color;
      res.preferredLanguage = loaded.preferredLanguage;
      res.hash = loaded.hash;
      res.almMapping = options.almMapping;
      if ("iconName" in loaded) {
        (res as AlmUser).iconName = loaded.iconName;
      }
    })
    .catch(() => {
      res.type = "unknown";
    });
  return res;
}

export async function loadUser(
  user: UserIdentifier,
  options?: { useCache?: boolean; mapBackendToAlm?: boolean },
): Promise<AuthUser> {
  if (isBackendUserId(user.id)) {
    return options?.mapBackendToAlm ? almUser(getAlmInfo()) : backendUser();
  }
  if (options?.useCache === false) {
    // bypass loadItem so we never wait on another caller's delayed in-flight lookup
    return users.add(user.id, await getUser(user));
  }
  // delaying inside the fetch keeps concurrent lookups deduplicated by the cache while waiting
  return await users.loadItem(user.id, async () => {
    await delayLookup();
    return getUser(user);
  });
}

// A broadcast event (e.g. a new sticky) makes every client look up the same unknown user at
// the same moment. A random delay spreads these lookups instead of hitting the server at once.
const MAX_LOOKUP_DELAY = 2000;

function delayLookup() {
  return new Promise((resolve) => setTimeout(resolve, Math.random() * MAX_LOOKUP_DELAY));
}

export function clearUserCache() {
  users.clear();
}

async function getUser(user: UserIdentifier): Promise<AuthUser> {
  try {
    const res = await api.get<ServerAuthUser>(`${baseURL}/${user.id}`, { maxRetries: 2 });
    return mapUser(res.data);
  } catch (e) {
    handleUserError(e, user.caller);
  }
  return uninitedUser(user, "unknown");
}

function handleUserError(e: unknown, caller: string) {
  if (e instanceof CancelError || e instanceof UnauthenticatedError) {
    // CancelError: most likely refreshing the token failed
    // don't log as we are already redirected to log out
    // UnauthenticatedError: let the errorHandler do the correct thing
    throw e;
  }
  // 404 response is legitimate (user can be deleted), don't log
  if (isHttpError(e) && e.response?.status === 404) {
    return;
  }
  handleError(e, `Could not get user from ${caller}`);
}

function uninitedUser(
  user: UserIdentifier & { name?: string },
  type: "unknown" | "loading",
): AuthUser {
  const { caller, ...userWithoutCaller } = user;
  return { ...baseUser, ...userWithoutCaller, type, name: mapUserName(user) };
}

export async function getOrgEntities(userId: string): Promise<UserOrgEntities> {
  const endpoint = `${baseURL}/${userId}/org_entities`;
  try {
    const res = await api.get<ServerUserOrgEntities>(endpoint);
    return mapOrgEntities(res.data);
  } catch (e) {
    captureException(e, {
      info: { message: "Could not fetch org entities of a user with id: " + userId },
    });
    return emptyUserOrgEntities;
  }
}
